/* ============================================================================
 * ez-engine-skirting.js — EZ-Snap RV-skirting configurator engine (theme asset, vanilla JS)
 * Source of truth: demo/app/lib/skirting.js.
 *
 * PORTED LOGIC: band resolution by RV length (bandForLength), the snap-pack combo
 * (studCombo — greedy over descending pack sizes), and the multi-line cart-payload
 * assembly (buildKitLines). The SKU SCHEME is ported; PRICE / VARIANT / BAND DATA is
 * INJECTED — Liquid builds the catalog from product objects at render:
 *
 *   const skirt = window.EZSnap.createSkirtingEngine({
 *     bands: [{ band:"1620", lo:16, hi:20, label:"16–20 ft", pS, pT,
 *               slides, material, snaps, clamp,
 *               variantS:{BK,BG,WH,GY}, variantT:{...} }, ...],  // 7 bands
 *     colors: [{ code, name }, ...],
 *     studPacks:  [200,150,100,50,25],
 *     clampPrice: { 25, 50, 75, 100 },             // CMP pipe-clamp pack prices
 *     velPrice:   { 10, 20 },                      // VEL Gecko Velcro prices
 *     bagPrice:   99,                              // storage-bag price
 *     variantId:  { kit(band,h,c), clamp(n), vel(ft), bag(h) }  // OPTIONAL resolvers
 *   });
 *
 * SNAP PACKS (2026-10-07): kits no longer carry snaps in the warehouse BOM. buildKitLines always emits the
 * kit's snap packs as DISPLAY lines (kitSnaps:true, price 0, no variantId) — two-code SKU
 * EZ-C-SNA-{n}-{cap}-{stud}, cap = kit color, stud = studColor (defaults to the kit color). The split is the
 * SAME studCombo(band.snaps) the old off-color stud add-on (EZ-C-SNS) used. The controller turns them into
 * real cart lines via window.EZKitSnaps.lines(); the server prices them.
 *
 * PUBLIC API:
 *   skirt.bandForLength(len)   -> the BANDS entry (or {under:true} / {oversize:true})
 *   skirt.studCombo(qty)       -> [200,50,...] pack breakdown covering qty (greedy desc)
 *   skirt.snapPacks(band)      -> [{ n:200, qty:2 }, { n:50, qty:1 }] — studCombo(band.snaps) grouped by size
 *   skirt.colorName(code)      -> display name
 *   skirt.slideText(band)      -> "Skirts up to N slide-outs" / "For RVs without slide-outs"
 *   skirt.kitSku(band,h,c)     -> "EZ-B-SKT-1620-S-BK"
 *   skirt.priceFor(band,h)     -> band price for height S|T (from injected data)
 *   skirt.buildKitLines(opts)  -> [{ sku, title, opts, qty, price, variantId? }, ...]
 * ============================================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module !== "undefined" && module.exports) module.exports = api; // node/gate
  if (root && typeof root === "object") {
    root.EZSnap = root.EZSnap || {};
    root.EZSnap.createSkirtingEngine = api.createSkirtingEngine;
  }
})(typeof window !== "undefined" ? window : this, function () {
  "use strict";

  function createSkirtingEngine(catalog) {
    catalog = catalog || {};
    var BANDS = (catalog.bands || []).slice();
    var COLORS = catalog.colors || [];
    var STUD_PACKS = catalog.studPacks || [200, 150, 100, 50, 25];
    var CLAMP_PRICE = catalog.clampPrice || {};
    var VEL_PRICE = catalog.velPrice || {};
    var BAG_PRICE = catalog.bagPrice;
    var VID = catalog.variantId || {};
    // Material-need fit constants (derived 2026-08-03 against 100 grounded RV personas + the physical
    // EZ-C-HIP hitch panel). need = 2*(len - noseOffset*isFifthWheel) + widthAdd + slidePer*slides + margin.
    //   noseOffset 6ft = the gooseneck the hitch kit covers (71" panel ≈ 5.92ft; FW overhang median 6.0ft).
    //   widthAdd 17ft = both 8.5ft ends.  slidePer 8ft = Jim's original per-slide wrap.
    //   margin 3ft = smallest value giving ZERO shortage across all personas without oversizing any rig.
    var FIT = Object.assign({ noseOffset: 6, widthAdd: 17, slidePer: 8, margin: 3 }, catalog.fit || {});

    function bandForLength(len) {
      if (len == null || isNaN(len)) return null;
      if (len < 16) return Object.assign({}, BANDS[0], { under: true });
      if (len >= 51) return { oversize: true };
      return BANDS.find(function (b) { return len >= b.lo && len < b.hi + 1; }) || null;
    }

    // The slide-out count Jim's length band assumes (its baked-in default) — the fallback when the
    // customer hasn't told us their real count.
    function assumedSlides(len) {
      var b = bandForLength(len);
      return (b && !b.oversize && b.slides) ? b.slides : 0;
    }
    // Computed skirting material (feet) a specific rig needs. slides == null → use the length band's assumed count.
    function needFor(o) {
      o = o || {};
      var len = o.len;
      if (len == null || isNaN(len)) return null;
      var type = o.type || "trailer";
      var slides = (o.slides == null) ? assumedSlides(len) : Number(o.slides);
      if (isNaN(slides) || slides < 0) slides = 0;
      var Lbody = (type === "fifth_wheel") ? Math.max(0, len - FIT.noseOffset) : len;
      return 2 * Lbody + FIT.widthAdd + FIT.slidePer * slides + FIT.margin;
    }
    // UPGRADE selection: the length band is the FLOOR (honest label, never a smaller kit than the
    // customer's length implies); bump UP only when the computed material need exceeds the floor's
    // material. Never selects below the length band, so it never undersells and a fifth-wheel is
    // covered even without the separate hitch kit. Returns a BANDS entry (may carry {under}) or {oversize}.
    function bandForBuild(o) {
      o = o || {};
      // Fifth-wheels floor at the BODY length (behind the gooseneck): the raised nose is closed by the
      // separate hitch-enclosure kit (offered as an in-builder upsell), NOT by the flat side skirting.
      // Travel trailers floor at the full length. WAS: all types floored at full length, which oversized
      // every fifth-wheel by ~1 band (~$150) and left the nose open — see hitch-audit/AUDIT-5thwheel-hitch.md.
      // needFor() already subtracts noseOffset for fifth_wheel, so the size-up path below stays consistent.
      var floorLen = (o.type === 'fifth_wheel') ? Math.max(0, o.len - FIT.noseOffset) : o.len;
      var base = bandForLength(floorLen);
      if (!base) return null;
      if (base.oversize) return base;
      // The length band already provisions for its ASSUMED slide count, so the DEFAULT state (slides
      // untouched, or fewer/equal to the assumed count) always keeps the length band — no spurious
      // size-up, and a length-valid rig is NEVER pushed to oversize by an assumed count. Only the
      // customer's EXPLICIT over-assumption (more slides than the band assumes) can bump the kit up.
      var assumed = assumedSlides(floorLen);
      if (o.slides == null || Number(o.slides) <= assumed) return base;
      var need = needFor(o);
      if (need == null || need <= base.material) return base;   // floor already covers the extra
      var baseIdx = 0;
      for (var k = 0; k < BANDS.length; k++) { if (BANDS[k].band === base.band) { baseIdx = k; break; } }
      for (var i = baseIdx + 1; i < BANDS.length; i++) {        // smallest covering band ABOVE the floor
        if (BANDS[i].material >= need) return base.under ? Object.assign({}, BANDS[i], { under: true }) : BANDS[i];
      }
      return { oversize: true, overBy: 'slides' };   // length is fine; EXPLICIT slides exceed the largest roll → contact sales
    }

    function studCombo(qty) {
      var rem = qty;
      var out = [];
      for (var i = 0; i < STUD_PACKS.length; i++) {
        var p = STUD_PACKS[i];
        while (rem >= p) { out.push(p); rem -= p; }
      }
      return out;
    }

    // The kit's snap packs: studCombo(band.snaps) grouped by size, largest first -> [{ n, qty }].
    // No new math — the same split the old off-color stud add-on bought.
    function snapPacks(b) {
      var counts = {}, order = [];
      studCombo((b && b.snaps) || 0).forEach(function (p) {
        if (!counts[p]) { counts[p] = 0; order.push(p); }
        counts[p]++;
      });
      return order.map(function (p) { return { n: p, qty: counts[p] }; });
    }

    function colorName(c) {
      var hit = COLORS.find(function (x) { return x.code === c; });
      return (hit && hit.name) || "";
    }

    function slideText(b) {
      return b && b.slides >= 1
        ? "Skirts up to " + b.slides + " slide-out" + (b.slides > 1 ? "s" : "")
        : "For RVs without slide-outs";
    }

    var kitSku = function (band, h, c) { return "EZ-B-SKT-" + band.band + "-" + h + "-" + c; };
    var priceFor = function (b, h) { return h === "T" ? b.pT : b.pS; };

    function buildKitLines(o) {
      o = o || {};
      var band = o.band;
      var height = o.height;
      var color = o.color;
      var clampsOn = !!o.clampsOn;
      var clampPack = o.clampPack;
      var studColor = o.studColor || color;   // studs default to the kit (material) color
      var velcroOn = !!o.velcroOn;
      var velFt = o.velFt || 10;
      var bagOn = !!o.bagOn;

      var lines = [{
        sku: kitSku(band, height, color),
        title: "EZ-Snap Skirting Kit",
        opts: band.label + " · " + (height === "T" ? 'Tall 60"' : 'Standard 46"') +
              " · " + colorName(color) + " · " + (o.slideLabel || slideText(band)),
        qty: 1,
        price: priceFor(band, height),
        variantId: VID.kit ? VID.kit(band, height, color) : undefined,
      }];

      // The kit's snap packs (always — they left the kit BOM). Display lines only: price 0, no variantId; the
      // controller resolves the real EZ-C-SNA variants via window.EZKitSnaps at add-to-cart.
      snapPacks(band).forEach(function (p) {
        lines.push({
          sku: "EZ-C-SNA-" + p.n + "-" + color + "-" + studColor, title: "Snap Pack",
          opts: p.n + "-count · " + colorName(color) + " caps · " + colorName(studColor) + " studs",
          qty: p.qty, price: 0, kitSnaps: true, packN: p.n, cap: color, stud: studColor,
        });
      });

      if (clampsOn) {
        var pack = clampPack || band.clamp;
        lines.push({
          sku: "EZ-B-CMP-" + pack, title: "Pipe Clamp Pack", opts: pack + "-count",
          qty: 1, price: CLAMP_PRICE[pack],
          variantId: VID.clamp ? VID.clamp(pack) : undefined,
        });
      }
      if (velcroOn) {
        lines.push({
          sku: "EZ-C-VEL-" + velFt, title: "Gecko Velcro™", opts: velFt + " ft",
          qty: 1, price: VEL_PRICE[velFt], variantId: VID.vel ? VID.vel(velFt) : undefined,
        });
      }
      if (bagOn) {
        lines.push({
          sku: "EZ-C-SKB-" + (height === "T" ? "T" : "S"), title: "Storage Bag",
          opts: height === "T" ? 'Tall 60"' : 'Standard 46"', qty: 1, price: BAG_PRICE,
          variantId: VID.bag ? VID.bag(height) : undefined,
        });
      }
      return lines;
    }

    return {
      bandForLength: bandForLength,
      bandForBuild: bandForBuild,
      needFor: needFor,
      assumedSlides: assumedSlides,
      fit: FIT,
      studCombo: studCombo,
      snapPacks: snapPacks,
      colorName: colorName,
      slideText: slideText,
      kitSku: kitSku,
      priceFor: priceFor,
      buildKitLines: buildKitLines,
      bands: BANDS,
    };
  }

  return { createSkirtingEngine: createSkirtingEngine };
});
